import {makeAutoObservable, reaction} from 'mobx'
import {Participant} from "./participant";
import {deserialize} from "serializr";
import {Addon} from "./addon";
import {ValidateAddParticipantController} from "./validateAddParticipantController";
import GA4Tracking from "../utils/ga4-tracking";

export class EventGroup {
  /**
   * @type {import('./participant').Participant[]}
   */
  participants = []
  /**
   * @type {import('./addon').Addon[]}
   */
  addons = []
  /**
   * @type {string}
   */
  id
  /**
   * @type {string}
   */
  title
  /**
   * @type {number}
   */
  pricePerParticipant
  vatPerParticipant
  _price
  first_date
  location
  discounts = new Map()
  subtitle = ''
  /**@type {{maillist?:boolean, gold?:boolean, silver?:boolean, extraordinary?:boolean}} */
  discountsAvailable = {}
  requiredFields = {}
  featured_image
  spots_left
  category
  chosen_addons = []
  validateAddParticipantController
  /**
   * @param {{
   *   id?:string,
   *   title?:string,
   *   price?:number,
   *   first_date?:string,
   *   location?:string,
   *   subtitle?:string,
   *   discountsAvailable?:{maillist:boolean, gold:boolean, silver:boolean, extraordinary:boolean},
   *   requiredFields?:object,
   *   featured_image?:string,
   *   vat?:string,
   *   spots_left?:number,
   *   category?:string,
   *   addons?:array,
   *   ga4_tracking_item?:object
   * }} obj
   */
  constructor({
    id,
    title,
    price,
    vat,
    first_date,
    location,
    subtitle,
    discountsAvailable,
    featured_image,
    spots_left,
    category,
    addons,
    ga4_tracking_item = {},
    requiredFields = {},
  } = {}) {
    makeAutoObservable(this)
    this.id = id
    this.title = title
    this.featured_image = featured_image
    this.pricePerParticipant = price
    this.vatPerParticipant = vat
    this._price = price
    this.first_date = first_date
    this.location = location
    this.subtitle = subtitle
    this.discountsAvailable = discountsAvailable ?? {}
    this.spots_left = spots_left
    this.category = category
    this.addons = deserialize(Addon, addons)
    this.ga4_tracking_item = ga4_tracking_item
    this.requiredFields = requiredFields

    this.validateAddParticipantController = new ValidateAddParticipantController()
  }

  /**
   * @param {import('./participant').Participant} participant
   */
  addParticipant(participant) {
    this.participants.push(participant)
  }

  /**
   * @param {import('./participant').Participant} participant
   */
  removeParticipant(participant) {
    this.participants.splice(this.participants.indexOf(participant), 1)
  }

  get participantCount() {
    return Math.max(this.participants.length, 1)
  }

  get totalDiscount() {
    return this.participants.reduce((acc, participant) => {
      return acc + participant.totalDiscount
    }, 0)
  }

  get mailDiscount() {
    return this.participants.reduce((acc, participant) => {
      return acc + participant.newsletterDiscount
    }, 0)
  }

  get couponDiscount() {
    return this.participants.reduce((acc, participant) => {
      return acc + participant.couponDiscount
    }, 0)
  }

  get loyaltyDiscount() {
    return this.participants.reduce((acc, participant) => {
      return acc + participant.loyaltyDiscount
    }, 0)
  }

  get price() {
    return this.pricePerParticipant * this.participantCount
  }

  get totalPrice() {
    return this.roundToTwoDecimals(this.price - this.totalDiscount)
  }

  get totalVat() {
    let vat = this.vatPerParticipant

    if (this.participants.length > 0) {
      vat = this.participants.reduce((acc, participant) => {
        return acc + participant.vat
      }, 0)
    }

    return this.roundToTwoDecimals(vat)
  }

  get addonsPrice() {
    return this.chosen_addons.reduce((acc, addon) => {
      return acc + addon.totalPriceExDiscount
    }, 0)
  }

  get addonsMailDiscount() {
    return this.chosen_addons.reduce((acc, addon) => {
      return acc + addon.mailDiscount
    }, 0)
  }

  get addonsCouponDiscount() {
    return this.chosen_addons.reduce((acc, addon) => {
      return acc + addon.couponDiscount
    }, 0)
  }

  get addonsLoyaltyDiscount() {
    return this.chosen_addons.reduce((acc, addon) => {
      return acc + addon.loyaltyDiscount
    }, 0)
  }

  get addonsDiscount() {
    return this.chosen_addons.reduce((acc, addon) => {
      return acc + addon.totalDiscount
    }, 0)
  }

  get totalAddonsPrice() {
    return this.roundToTwoDecimals(this.addonsPrice - this.addonsDiscount)
  }

  get totalAddonsVat() {
    return this.chosen_addons.reduce((acc, addon) => {
      return acc + addon.totalVat
    }, 0)
  }

  get priceIncludingAddons() {
    return this.roundToTwoDecimals(this.price + this.addonsPrice)
  }

  get totalPriceIncludingAddons() {
    return this.roundToTwoDecimals(this.totalPrice + this.totalAddonsPrice)
  }

  get mailDiscountIncludingAddons() {
    return this.roundToTwoDecimals(this.mailDiscount + this.addonsMailDiscount)
  }

  get couponDiscountIncludingAddons() {
    return this.roundToTwoDecimals(this.couponDiscount + this.addonsCouponDiscount)
  }

  get loyaltyDiscountIncludingAddons() {
    return this.roundToTwoDecimals(this.loyaltyDiscount + this.addonsLoyaltyDiscount)
  }

  get totalDiscountIncludingAddons() {
    return this.roundToTwoDecimals(this.totalDiscount + this.addonsDiscount)
  }

  get totalVatIncludingAddons() {
    return this.roundToTwoDecimals(this.totalVat + this.totalAddonsVat)
  }

  get availableAddons() {
    return this.addons.filter(addon => addon.spots_left >= this.participants.length)
  }

  roundToTwoDecimals(number) {
    return +(Math.round(+(number + 'e+2')) + 'e-2') // https://www.delftstack.com/howto/javascript/javascript-round-to-2-decimal-places/#using-the-custom-function-to-round-a-number-to2-decimal-places-in-javascript
  }


  hasAddon(addon) {
    return this.chosen_addons.some((element) => element.id === addon.id)
  }

  toggleAddon(addon) {
    if (this.hasAddon(addon)) {
      this.removeAddon(addon)
    } else {
      this.addAddon(addon)
    }
  }

  removeAddon(addon) {
    this.chosen_addons.splice(this.chosen_addons.findIndex((el) => el.id === addon.id), 1)

    if(typeof gtag === "function") {
      gtag('config', 'UA-36099469-3', {
        'page_title' : 'Fjern tilvalg',
        'page_path': '/remove-addon/'
      });
    }
  }

  addAddon(addon) {
    this.chosen_addons.push(addon)

    if(typeof gtag === "function") {
      gtag('config', 'UA-36099469-3', {
        'page_title' : 'Tilføj tilvalg',
        'page_path': '/add-addon/'
      });
    }
  }

  getChosenAddon(addon) {
    const id = addon.id ?? addon
    return this.chosen_addons.find((el) => el.id === id)
  }

  async updateAddonParticipants(addon, customer, coupon) {
    addon = this.getChosenAddon(addon)

    if(typeof addon === 'undefined') {
      return
    }

    if(this.participants.length < 1) {
      addon.participants.clear()

      return
    }

    await this.validateAddParticipantController.validate(
      [addon],
      this.participants,
      customer,
      coupon,
      false
    );

    if(this.validateAddParticipantController.isAllValid) {
      addon.participants.clear()

      this.participants.forEach(participant => {
        const _participant = new Participant(participant.id)
        _participant.price = addon.price
        _participant.vat = addon.vat
        _participant.discounts = new Map()
        _participant.email = participant.email
        _participant.name = participant.name
        _participant.position = participant.position
        _participant.updateContent(this.validateAddParticipantController.validation_data?.[addon.id], false)
        addon.addParticipant(_participant)
      })

    }
  }
}
